import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Users, RotateCcw, Lightbulb, User, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import OptimizedImage from '@/components/OptimizedImage';
import MaatwerkOpleidingDialog from '@/components/MaatwerkOpleidingDialog';
import { cn } from '@/lib/utils';
import heropaars from '@/assets/heropaars.png';
import heropaarsWebp from '@/assets/heropaars.webp';
import teamcoachingVisual from '@/assets/systemique/teamcoaching-visual.webp';
import { SEO } from '@/components/SEO';
import { SITE_NAME, SITE_ORIGIN } from '@/seo/siteConfig';

const TEAMCOACHING_SEO_TITLE =
  'Systemische teamcoaching Amsterdam, Haarlem, Castricum & Noord-Holland';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      // Responsive gradient size: smaller on mobile, larger on desktop
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300); // mobile
      } else if (width < 1024) {
        setGradientSize(450); // tablet
      } else {
        setGradientSize(600); // desktop
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

function Teamcoaching() {
  const [maatwerkDialogOpen, setMaatwerkDialogOpen] = useState(false);
  
  const issues = [
    {
      title: "Gedoe in de samenwerking",
      desc: "Spanningen, conflicten of een sfeer die niet lekker voelt.",
      icon: Users
    },
    {
      title: "Niet mee willen bewegen",
      desc: "Weerstand tegen verandering of nieuwe plannen.",
      icon: Target
    },
    {
      title: "In- en uitstroom",
      desc: "Veel verloop, of moeite met het onboarden van nieuwe mensen.",
      icon: RotateCcw
    },
    {
      title: "Verschillen van inzicht",
      desc: "Op directie- of bestuursniveau niet op één lijn komen.",
      icon: Lightbulb
    },
    {
      title: "Hoog verzuim",
      desc: "Een signaal dat er iets in de onderstroom aandacht vraagt.",
      icon: User
    }
  ];

  return (
    <>
      <SEO 
        title={TEAMCOACHING_SEO_TITLE}
        ogTitle={TEAMCOACHING_SEO_TITLE}
        description="Systemische teamcoaching in Amsterdam, Haarlem, Castricum en heel Noord-Holland. Voor teams met hoog verloop, conflicten en samenwerkingsproblemen. Teamcoaching die de onderstroom zichtbaar maakt. Begeleid door Marthe van der Molen."
        keywords="teamcoaching systemisch Amsterdam, systemische teamcoaching Haarlem, teamcoaching Castricum, teamcoaching systemisch Noord-Holland, systemische teamcoaching Zaanstreek, teamcoaching Alkmaar, teamcoaching hoog verloop, systemische teambegeleiding Amsterdam, teamcoaching Zaandam, teamcoaching Hoofddorp, systemisch werk teams, teamcoaching conflicten"
        canonical="https://organisatieopstellingen.com/teamcoaching"
        ogDescription="Systemische teamcoaching in Amsterdam, Haarlem, Castricum en Noord-Holland. Voor teams met conflicten, hoog verloop of samenwerkingsproblemen."
      />
      
      {/* FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wat is systemische teamcoaching?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Systemische teamcoaching is een methodiek die kijkt naar de onderstroom en dynamieken in teams. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar. Systemische teamcoaching helpt teams om de bron van problemen te vinden, niet alleen de symptomen. Organisatieopstellingen biedt systemische teamcoaching vanuit Castricum voor teams in Amsterdam, Haarlem en heel Noord-Holland."
                }
              },
              {
                "@type": "Question",
                "name": "Wanneer is systemische teamcoaching geschikt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Systemische teamcoaching is geschikt wanneer teams te maken hebben met conflicten, hoog verloop, communicatieproblemen, of wanneer er sprake is van onveiligheid in het team. Het is ook effectief bij reorganisaties, fusies, of wanneer teams vastlopen in processen. Organisatieopstellingen biedt teamcoaching aan in Amsterdam, Haarlem, Castricum en de regio Noord-Holland."
                }
              },
              {
                "@type": "Question",
                "name": "Hoe werkt systemische teamcoaching?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Systemische teamcoaching maakt gebruik van opstellingen, systemische interventies en reflectie om de onderstroom van het team zichtbaar te maken. Door middel van representanten of symbolen worden teamdynamieken, rollen en verhoudingen in beeld gebracht. Dit levert vaak verrassende inzichten op over de werkelijke oorzaken van problemen. Organisatieopstellingen begeleidt teamcoaching trajecten in Amsterdam, Haarlem, Castricum en Noord-Holland; trajecten duren doorgaans 1 tot 6 sessies."
                }
              }
            ]
          })
        }}
      />
      
      {/* Service Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Teamcoaching & Organisatieopstellingen",
            "description": "Professionele begeleiding van teams en organisaties bij complexe vraagstukken middels systemisch werk en opstellingen.",
            "provider": {
              "@type": "Organization",
              "name": SITE_NAME,
            "url": SITE_ORIGIN,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Willem de Zwijgerlaan 14",
                "addressLocality": "Castricum",
                "postalCode": "1901 CR",
                "addressRegion": "Noord-Holland",
                "addressCountry": "NL"
              },
              "telephone": "+31-6-12991453",
              "email": "marthe@systemique.nl"
            },
            "areaServed": [
              {
                "@type": "Country",
                "name": "Nederland"
              },
              {
                "@type": "State",
                "name": "Noord-Holland"
              },
              {
                "@type": "City",
                "name": "Castricum"
              },
              {
                "@type": "City",
                "name": "Amsterdam"
              },
              {
                "@type": "City",
                "name": "Haarlem"
              }
            ],
            "serviceType": "Teamcoaching Systemisch",
            "audience": {
              "@type": "Audience",
              "audienceType": "Bestuurders, leidinggevenden, managers en teams"
            }
          })
        }}
      />
      
      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${SITE_ORIGIN}/`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Aanbod",
                "item": `${SITE_ORIGIN}/aanbod`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Teamcoaching Systemisch",
                "item": `${SITE_ORIGIN}/teamcoaching`
              }
            ]
          })
        }}
      />
      
      <div className="min-h-screen bg-[#FAEBEE]">
        {/* Custom Luxury Header */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
          <div className="absolute inset-0 z-0">
            <GrainTexture opacity={0.2} />
            <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
          </div>
          
          <div className="container mx-auto relative z-10 text-center">
            <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
              <Link to="/">
                <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
              </Link>
            </Button>
            
            <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
              Teamcoaching <span className="italic font-light font-serif text-[#F9EBEF]">systemisch</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
              Versterk de teamsamenwerking door de onderstroom aan te pakken.
            </p>
          </div>
        </section>
        
        <section className="py-24 px-6 relative">
          <GrainTexture opacity={0.1} />
          <div className="container mx-auto max-w-6xl relative z-10">
             
             {/* Intro met twee kolommen */}
             <div className="grid md:grid-cols-2 gap-16 mb-24 items-center">
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-6 leading-tight">
                    Wanneer is systemische <span className="italic font-light font-serif">teamcoaching</span> geschikt?
                  </h2>
                  <div className="w-20 h-1 bg-[#854C73]/20 mb-6 rounded-full"></div>
                  <p className="text-lg text-[#6b4912] leading-relaxed font-light mb-6">
                    <strong>Systemische teamcoaching</strong> is geschikt wanneer teams vastlopen ondanks goede
                    intenties: bij conflicten, hoog verloop, onveilige cultuur, na reorganisatie of wanneer
                    communicatie blijft steken. Door uit te zoomen kijken we naar het hele systeem en ontdekken we
                    waar de kiem van de (nieuwe) beweging zit.
                  </p>
                  <p className="text-lg text-[#6b4912] leading-relaxed font-light">
                    Marthe van der Molen begeleidt trajecten vanuit Castricum voor teams in Amsterdam, Haarlem en
                    heel Noord-Holland; sessies duren doorgaans 2 tot 3 uur, trajecten 1 tot 6 sessies afhankelijk
                    van de vraag. Op locatie of in-house in heel Nederland.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-3xl shadow-lg border border-[#854C73]/5 relative overflow-hidden">
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#F9EBEF] rounded-full -z-10"></div>
                    <OptimizedImage 
                      src={teamcoachingVisual} 
                      webpSrc={teamcoachingVisual}
                      alt="teamcoaching sessie bedrijf"
                      className="w-full h-auto rounded-2xl"
                      width={1067}
                      height={1600}
                    />
                </div>
             </div>

             {/* Uitgebreide Cards Grid (Spotlight) */}
             <div className="mb-16 text-center">
               <h3 className="font-serif text-3xl text-[#854C73] mb-4">Wanneer helpt systemische teamcoaching?</h3>
               <p className="text-[#6b4912]/70 max-w-2xl mx-auto">Herkenbare situaties waar <strong>systemische teamcoaching</strong> het verschil maakt.</p>
             </div>

             <div className="grid md:grid-cols-3 gap-6 mb-24">
               {issues.map((item, i) => {
                 const Icon = item.icon;
                 return (
                   <SpotlightCard key={i} className="p-8 bg-white h-full flex flex-col items-start text-left">
                     <div className="p-3 rounded-2xl bg-[#FAEBEE] text-[#854C73] mb-6">
                       <Icon className="w-8 h-8" strokeWidth={1.5} />
                     </div>
                     <h3 className="font-serif text-xl text-[#854C73] mb-3 font-bold">{item.title}</h3>
                     <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
                   </SpotlightCard>
                 );
               })}
             </div>

             {/* Maatwerk Section (Dark luxury style) */}
             <div className="bg-[#854C73] rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 pointer-events-none">
                  <GrainTexture opacity={0.2} />
                  <div className="absolute top-0 left-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                  <div className="absolute bottom-0 right-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
                </div>
                
                <div className="relative z-10 max-w-3xl mx-auto">
                  <Target className="w-16 h-16 text-[#FAEBEE] mx-auto mb-8 opacity-90" strokeWidth={1} />
                  <h3 className="font-serif text-3xl md:text-5xl text-[#FAEBEE] mb-6">
                    Altijd <span className="italic font-light font-serif">Maatwerk</span>
                  </h3>
                  <p className="text-[#FAEBEE]/80 text-lg md:text-xl leading-relaxed mb-6 font-light">
                    Na een intakegesprek ontvang je een voorstel dat precies aansluit bij het vraagstuk.
                  </p>
                  <p className="text-[#FAEBEE]/70 text-base md:text-lg leading-relaxed mb-10 font-light">
                    Ook organiseren we regelmatig opstellingenmiddagen waar organisatieopstellingen plaatsvinden. 
                    <Link to="/agenda" className="text-[#FAEBEE] hover:underline font-semibold ml-1">Bekijk mijn agenda</Link> voor komende data.
                  </p>
                  <Button 
                    variant="white" 
                    size="lg" 
                    className="px-10 py-6 text-lg shadow-xl hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => setMaatwerkDialogOpen(true)}
                  >
                    Vraag offerte aan
                  </Button>
                </div>
             </div>

          </div>
        </section>
      </div>
      
      <MaatwerkOpleidingDialog open={maatwerkDialogOpen} onOpenChange={setMaatwerkDialogOpen} />
    </>
  );
}

export default Teamcoaching;
